import React, { useState, useEffect, useRef } from "react";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Move,
  Square,
  Circle,
  Coffee,
  ChefHat,
  CreditCard,
  ZoomIn,
  ZoomOut,
  RotateCw,
  DoorOpen,
} from "lucide-react";
import { RestaurantTable, Staff } from "../types";
import { supabase } from "../lib/supabase";

interface FloorplanEditorProps {
  currentStaff: Staff;
  onClose: () => void;
  tables: RestaurantTable[];
  onTablesUpdate: (tables: RestaurantTable[]) => void;
}

interface FloorplanItem {
  id: string;
  type: "table" | "bar" | "kitchen" | "entrance" | "till" | "wall" | "counter";
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  shape?: "circle" | "square" | "rectangle";
  table?: RestaurantTable;
  label?: string;
  color?: string;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

const FloorplanEditor: React.FC<FloorplanEditorProps> = ({ currentStaff, onClose, tables, onTablesUpdate }) => {
  const canvasRef = useRef<HTMLDivElement>(null);

  const [floorplanItems, setFloorplanItems] = useState<FloorplanItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeMode, setResizeMode] = useState<"move" | "resize">("move");
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);

  const [zoom, setZoom] = useState<number>(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const [showAddTable, setShowAddTable] = useState(false);
  const [showAddElement, setShowAddElement] = useState(false);
  const [showEditTable, setShowEditTable] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [newTable, setNewTable] = useState({
    table_number: "",
    seats: 4,
    shape: "circle" as "circle" | "square" | "rectangle",
  });

  const [newElement, setNewElement] = useState({
    type: "bar" as "bar" | "kitchen" | "entrance" | "till" | "wall" | "counter",
    label: "",
    width: 150,
    height: 80,
  });

  useEffect(() => {
    loadFloorplan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom((prev) => Math.max(0.3, Math.min(3, prev * delta)));
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener("wheel", handleWheel, { passive: false });
      return () => canvas.removeEventListener("wheel", handleWheel as any);
    }
  }, []);

  useEffect(() => {
    if (floorplanItems.length === 0) loadFloorplan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  const loadFloorplan = () => {
    loadFloorplanFromDatabase();
  };

  const loadFloorplanFromDatabase = async () => {
    try {
      const { data, error } = await supabase
        .from("floorplan_items")
        .select(
          `
          *,
          restaurant_tables!floorplan_items_table_id_fkey (*)
        `
        )
        .order("created_at");

      if (error) {
        console.error("Error fetching floorplan:", error);
        createInitialFloorplan();
      } else if (data && data.length > 0) {
        const transformedItems: FloorplanItem[] = (data as any[]).map((item) => ({
          id: item.item_id,
          type: item.type as any,
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.height,
          rotation: item.rotation,
          shape: item.shape as any,
          table: item.restaurant_tables || undefined,
          label: item.label,
          color: item.color,
        }));
        setFloorplanItems(transformedItems);
      } else {
        createInitialFloorplan();
      }
    } catch (error) {
      console.error("Error loading floorplan:", error);
      createInitialFloorplan();
    }
  };

  const createInitialFloorplan = () => {
    const tableItems: FloorplanItem[] = tables.map((table, index) => ({
      id: `table-${table.id}`,
      type: "table",
      x: (index % 5) * 120 + 100,
      y: Math.floor(index / 5) * 120 + 150,
      width: 96,
      height: 96,
      rotation: 0,
      shape: "circle",
      table,
    }));

    const roomItems: FloorplanItem[] = [
      { id: "entrance-1", type: "entrance", x: 60, y: 60, width: 160, height: 36, rotation: 0, label: "Entrance", color: "#10B981" },
      { id: "kitchen-1", type: "kitchen", x: 620, y: 60, width: 220, height: 140, rotation: 0, label: "Kitchen", color: "#EF4444" },
      { id: "bar-1", type: "bar", x: 320, y: 430, width: 260, height: 72, rotation: 0, label: "Bar", color: "#8B5CF6" },
      { id: "till-1", type: "till", x: 120, y: 430, width: 120, height: 72, rotation: 0, label: "Till", color: "#F59E0B" },
    ];

    setFloorplanItems([...tableItems, ...roomItems]);
  };

  const saveFloorplan = async () => {
    try {
      const { error: deleteError } = await supabase
        .from("floorplan_items")
        .delete()
        .neq("id", "00000000-0000-0000-0000-000000000000");

      if (deleteError) console.error("Error deleting existing floorplan:", deleteError);

      const itemsToInsert = floorplanItems.map((item) => ({
        item_id: item.id,
        type: item.type,
        x: Math.round(item.x),
        y: Math.round(item.y),
        width: Math.round(item.width),
        height: Math.round(item.height),
        rotation: Math.round(item.rotation),
        shape: item.shape,
        table_id: item.table?.id || null,
        label: item.label,
        color: item.color,
      }));

      const { error: insertError } = await supabase.from("floorplan_items").insert(itemsToInsert);

      if (insertError) {
        console.error("Error saving floorplan:", insertError);
        alert("Error saving floorplan. Please try again.");
        return;
      }

      const tableUpdates = floorplanItems
        .filter((item) => item.type === "table" && item.table)
        .map((item) => ({
          id: item.table!.id,
          x_position: item.x,
          y_position: item.y,
        }));

      for (const update of tableUpdates) {
        await supabase
          .from("restaurant_tables")
          .update({ x_position: Math.round(update.x_position), y_position: Math.round(update.y_position) })
          .eq("id", update.id);
      }

      const updatedTables = tables.map((table) => {
        const floorplanTable = floorplanItems.find((item) => item.type === "table" && item.table?.id === table.id);
        if (floorplanTable) {
          return { ...table, x_position: floorplanTable.x, y_position: floorplanTable.y };
        }
        return table;
      });

      onTablesUpdate(updatedTables);
      alert("Floorplan saved successfully!");
    } catch (error) {
      console.error("Error saving floorplan:", error);
      alert("Error saving floorplan. Please try again.");
    }
  };

  const handleMouseDown = (e: React.MouseEvent, itemId: string) => {
    if (!editMode) return;

    const target = e.target as HTMLElement;
    if (target.classList.contains("resize-handle")) {
      setResizeHandle(itemId);
      setSelectedItem(itemId);
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const item = floorplanItems.find((i) => i.id === itemId);
    if (!item) return;

    setDraggedItem(itemId);
    setSelectedItem(itemId);
    setDragOffset({
      x: (e.clientX - rect.left - panOffset.x) / zoom - item.x,
      y: (e.clientY - rect.top - panOffset.y) / zoom - item.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const deltaX = e.clientX - panStart.x;
      const deltaY = e.clientY - panStart.y;
      setPanOffset({ x: panOffset.x + deltaX, y: panOffset.y + deltaY });
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }

    if ((!draggedItem && !resizeHandle) || !editMode) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    if (resizeHandle) {
      const mouseX = (e.clientX - rect.left - panOffset.x) / zoom;
      const mouseY = (e.clientY - rect.top - panOffset.y) / zoom;

      setFloorplanItems((items) =>
        items.map((item) => {
          if (item.id === resizeHandle) {
            const newWidth = Math.max(40, mouseX - item.x);
            const newHeight = Math.max(40, mouseY - item.y);
            return { ...item, width: newWidth, height: newHeight };
          }
          return item;
        })
      );
    } else if (draggedItem) {
      const newX = (e.clientX - rect.left - panOffset.x) / zoom - dragOffset.x;
      const newY = (e.clientY - rect.top - panOffset.y) / zoom - dragOffset.y;

      setFloorplanItems((items) =>
        items.map((item) => (item.id === draggedItem ? { ...item, x: Math.max(0, newX), y: Math.max(0, newY) } : item))
      );
    }
  };

  const handleMouseUp = () => {
    setDraggedItem(null);
    setResizeHandle(null);
    setIsPanning(false);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current || (e.target as HTMLElement).classList.contains("canvas-background")) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      setSelectedItem(null);
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev / 1.2, 0.3));
  const resetView = () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const addNewTable = () => {
    if (!newTable.table_number) return;

    const newTableData: RestaurantTable = {
      id: Date.now().toString(),
      table_number: parseInt(newTable.table_number),
      seats: newTable.seats,
      section: "",
      status: "available",
      x_position: 200,
      y_position: 200,
      created_at: new Date().toISOString(),
    };

    const tableSize = newTable.shape === "circle" ? 96 : newTable.shape === "square" ? 96 : 140;

    const newFloorplanItem: FloorplanItem = {
      id: `table-${newTableData.id}`,
      type: "table",
      x: 200,
      y: 200,
      width: tableSize,
      height: newTable.shape === "rectangle" ? 78 : tableSize,
      rotation: 0,
      shape: newTable.shape,
      table: newTableData,
    };

    setFloorplanItems([...floorplanItems, newFloorplanItem]);
    onTablesUpdate([...tables, newTableData]);
    setShowAddTable(false);
    setNewTable({ table_number: "", seats: 4, shape: "circle" });
  };

  const addNewElement = () => {
    if (!newElement.label && newElement.type !== "wall") return;

    const elementColors = {
      bar: "#8B5CF6",
      kitchen: "#EF4444",
      entrance: "#10B981",
      till: "#F59E0B",
      wall: "#6B7280",
      counter: "#3B82F6",
    };

    const newFloorplanItem: FloorplanItem = {
      id: `${newElement.type}-${Date.now()}`,
      type: newElement.type,
      x: 300,
      y: 300,
      width: newElement.width,
      height: newElement.height,
      rotation: 0,
      label: newElement.label || newElement.type,
      color: elementColors[newElement.type],
    };

    setFloorplanItems([...floorplanItems, newFloorplanItem]);
    setShowAddElement(false);
    setNewElement({ type: "bar", label: "", width: 150, height: 80 });
  };

  const updateTableDetails = () => {
    if (!selectedItem) return;

    const item = floorplanItems.find((i) => i.id === selectedItem);
    if (!item || !item.table) return;

    setFloorplanItems((items) =>
      items.map((i) =>
        i.id === selectedItem && i.table
          ? { ...i, table: { ...i.table, seats: newTable.seats }, shape: newTable.shape }
          : i
      )
    );

    const updatedTables = tables.map((table) => (table.id === item.table?.id ? { ...table, seats: newTable.seats } : table));
    onTablesUpdate(updatedTables);

    setShowEditTable(false);
    setSelectedItem(null);
  };

  const deleteItem = (itemId: string) => {
    const item = floorplanItems.find((i) => i.id === itemId);
    if (item?.type === "table" && item.table) {
      onTablesUpdate(tables.filter((t) => t.id !== item.table?.id));
    }
    setFloorplanItems((items) => items.filter((i) => i.id !== itemId));
    setSelectedItem(null);
    setShowEditTable(false);
  };

  const getTableColor = (status: string) => {
    switch (status) {
      case "available":
        return "#10B981";
      case "occupied":
        return "#EF4444";
      case "reserved":
        return "#F59E0B";
      case "cleaning":
        return "#6B7280";
      default:
        return "#6B7280";
    }
  };

  const tableStatusMeta = (status: string) => {
    switch (status) {
      case "available":
        return {
          ring: "ring-emerald-200",
          badge: "bg-emerald-100 text-emerald-800",
          dot: "bg-emerald-500",
        };
      case "reserved":
        return {
          ring: "ring-amber-200",
          badge: "bg-amber-100 text-amber-800",
          dot: "bg-amber-500",
        };
      case "occupied":
        return {
          ring: "ring-rose-200",
          badge: "bg-rose-100 text-rose-800",
          dot: "bg-rose-500",
        };
      case "cleaning":
      default:
        return {
          ring: "ring-slate-200",
          badge: "bg-slate-100 text-slate-800",
          dot: "bg-slate-500",
        };
    }
  };

  const getItemStyle = (item: FloorplanItem) => {
    const baseStyle: React.CSSProperties = {
      position: "absolute",
      left: item.x,
      top: item.y,
      width: item.width,
      height: item.height,
      transform: `rotate(${item.rotation}deg)`,
      cursor: editMode ? (resizeMode === "resize" ? "nw-resize" : "move") : "pointer",
      transition: draggedItem === item.id || resizeHandle === item.id ? "none" : "transform 0.15s ease, box-shadow 0.15s ease",
      zIndex: selectedItem === item.id ? 10 : 1,
      userSelect: "none",
    };

    if (item.type !== "table") return baseStyle;
    return baseStyle;
  };

  const elementTypes = [
    { type: "bar", label: "Bar", icon: Coffee, color: "#8B5CF6" },
    { type: "kitchen", label: "Kitchen", icon: ChefHat, color: "#EF4444" },
    { type: "entrance", label: "Entrance", icon: DoorOpen, color: "#10B981" },
    { type: "till", label: "Till/POS", icon: CreditCard, color: "#F59E0B" },
    { type: "counter", label: "Counter", icon: Square, color: "#3B82F6" },
    { type: "wall", label: "Wall", icon: Square, color: "#6B7280" },
  ] as const;

  const selected = selectedItem ? floorplanItems.find((i) => i.id === selectedItem) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-center justify-center">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl h-[95vh] ring-1 ring-slate-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200 bg-white flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm text-slate-500">Tables</div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Restaurant Floorplan</h2>
            <p className="text-sm text-slate-500 mt-1">Drag items to arrange your layout. Save when you’re done.</p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {currentStaff.role === "manager" && (
              <>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className={cx(
                    "px-4 py-2.5 rounded-2xl font-extrabold transition-colors inline-flex items-center gap-2",
                    editMode ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-slate-900 text-white hover:bg-slate-800"
                  )}
                >
                  <Edit size={18} />
                  {editMode ? "Exit Edit" : "Edit Layout"}
                </button>

                {editMode && (
                  <>
                    <button
                      onClick={() => setShowAddTable(true)}
                      className="px-4 py-2.5 rounded-2xl font-extrabold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
                    >
                      <Plus size={18} />
                      Add Table
                    </button>

                    <button
                      onClick={() => setShowAddElement(true)}
                      className="px-4 py-2.5 rounded-2xl font-extrabold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors inline-flex items-center gap-2"
                    >
                      <Plus size={18} />
                      Add Element
                    </button>

                    <button
                      onClick={saveFloorplan}
                      className="px-4 py-2.5 rounded-2xl font-extrabold bg-blue-600 text-white hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                    >
                      <Save size={18} />
                      Save
                    </button>
                  </>
                )}
              </>
            )}

            {/* Zoom controls */}
            <div className="hidden md:flex items-center gap-2 bg-slate-100 ring-1 ring-slate-200 rounded-2xl p-1.5">
              <button
                onClick={handleZoomOut}
                className="p-2 rounded-xl text-slate-700 hover:bg-white hover:shadow-sm transition-colors"
                title="Zoom out"
              >
                <ZoomOut size={18} />
              </button>

              <div className="px-2 min-w-[72px] text-center font-extrabold tabular-nums text-slate-900">
                {Math.round(zoom * 100)}%
              </div>

              <button
                onClick={handleZoomIn}
                className="p-2 rounded-xl text-slate-700 hover:bg-white hover:shadow-sm transition-colors"
                title="Zoom in"
              >
                <ZoomIn size={18} />
              </button>

              <button
                onClick={resetView}
                className="p-2 rounded-xl text-slate-700 hover:bg-white hover:shadow-sm transition-colors"
                title="Reset view"
              >
                <RotateCw size={18} />
              </button>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-2xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              title="Close"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 p-6 overflow-hidden">
          <div
            ref={canvasRef}
            className="relative w-full h-full bg-white rounded-3xl ring-1 ring-slate-200 shadow-sm overflow-hidden cursor-grab active:cursor-grabbing"
            style={{ minHeight: "600px", minWidth: "1000px" }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseDown={handleCanvasMouseDown}
          >
            {/* Zoomable container */}
            <div
              className="absolute inset-0"
              style={{
                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                transformOrigin: "0 0",
                width: "2000px",
                height: "1500px",
              }}
            >
              {/* Grid background */}
              <div
                className="canvas-background absolute inset-0"
                style={{
                  backgroundImage: `
                    radial-gradient(circle at 1px 1px, rgba(15,23,42,0.08) 1px, transparent 0),
                    radial-gradient(circle at 1px 1px, rgba(15,23,42,0.05) 1px, transparent 0)
                  `,
                  backgroundSize: "28px 28px, 56px 56px",
                }}
              />

              {/* Items */}
              {floorplanItems.map((item) => {
                const isTable = item.type === "table" && item.table;
                const isSelected = selectedItem === item.id;

                if (isTable) {
                  const status = item.table!.status;
                  const meta = tableStatusMeta(status);
                  const shape = item.shape || "circle";

                  return (
                    <div
                      key={item.id}
                      style={getItemStyle(item)}
                      onMouseDown={(e) => handleMouseDown(e, item.id)}
                      onClick={() => {
                        if (!editMode) return;
                        setSelectedItem(item.id);
                      }}
                      className={cx(
                        "group bg-white ring-2 shadow-sm flex items-center justify-center text-slate-900",
                        "hover:shadow-md transition-shadow",
                        meta.ring,
                        shape === "circle" ? "rounded-full" : "rounded-2xl",
                        isSelected ? "ring-blue-400" : "",
                        editMode ? "" : "cursor-default"
                      )}
                    >
                      <div className="text-center px-2">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-2xl font-extrabold tabular-nums">{item.table!.table_number}</span>
                          <span className={cx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-extrabold capitalize", meta.badge)}>
                            <span className={cx("h-2 w-2 rounded-full", meta.dot)} />
                            {status}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center justify-center text-sm font-semibold text-slate-600">
                          <Users size={14} className="mr-1.5 text-slate-500" />
                          {item.table!.seats}
                        </div>
                      </div>

                      {/* Resize handle */}
                      {editMode && isSelected && (
                        <div
                          className="resize-handle absolute -bottom-2 -right-2 w-5 h-5 rounded-full bg-blue-600 ring-2 ring-white shadow cursor-nw-resize"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setResizeHandle(item.id);
                          }}
                          title="Resize"
                        />
                      )}
                    </div>
                  );
                }

                // Room element
                return (
                  <div
                    key={item.id}
                    style={{
                      ...getItemStyle(item),
                      borderRadius: 18,
                      background: "rgba(15,23,42,0.72)",
                      color: "white",
                      boxShadow: isSelected ? "0 0 0 3px rgba(59,130,246,0.9), 0 10px 24px rgba(0,0,0,0.18)" : "0 10px 24px rgba(0,0,0,0.14)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 8,
                      cursor: editMode ? "move" : "default",
                    }}
                    onMouseDown={(e) => handleMouseDown(e, item.id)}
                    onClick={() => editMode && setSelectedItem(item.id)}
                  >
                    <div className="text-center px-2">
                      <div className="text-sm font-extrabold">{item.label}</div>
                      <div className="text-[11px] text-white/70 mt-0.5">{item.type}</div>
                    </div>

                    {editMode && isSelected && (
                      <div
                        className="resize-handle absolute -bottom-2 -right-2 w-5 h-5 rounded-full bg-blue-600 ring-2 ring-white shadow cursor-nw-resize"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setResizeHandle(item.id);
                        }}
                        title="Resize"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer / Legend */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm font-semibold text-slate-700">
            {[
              { label: "Available", dot: "bg-emerald-500" },
              { label: "Occupied", dot: "bg-rose-500" },
              { label: "Reserved", dot: "bg-amber-500" },
              { label: "Cleaning", dot: "bg-slate-500" },
            ].map((s) => (
              <div key={s.label} className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 bg-slate-50 ring-1 ring-slate-200">
                <span className={cx("h-3 w-3 rounded-full", s.dot)} />
                <span>{s.label}</span>
              </div>
            ))}

            <div className="hidden md:inline-flex items-center gap-2 rounded-2xl px-3 py-2 bg-slate-50 ring-1 ring-slate-200 text-slate-600">
              <ZoomIn size={16} className="text-slate-500" />
              Ctrl + Scroll to zoom • Drag to pan
            </div>

            {editMode && (
              <div className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 bg-blue-50 ring-1 ring-blue-200 text-blue-700">
                <Move size={16} />
                Drag to move • Blue dot to resize
              </div>
            )}
          </div>

          {/* Selected item strip */}
          {selected && editMode && (
            <div className="mt-4 rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-extrabold text-slate-900 truncate">
                  {selected.type === "table" && selected.table ? `Table ${selected.table.table_number}` : selected.label || selected.type}
                </div>
                <div className="text-sm text-slate-600">
                  Position: ({Math.round(selected.x)}, {Math.round(selected.y)}) • Size: {Math.round(selected.width)}×{Math.round(selected.height)}
                  {selected.table ? ` • ${selected.table.seats} seats` : ""}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {selected.type === "table" && (
                  <button
                    onClick={() => {
                      setNewTable({
                        table_number: selected.table?.table_number?.toString() || "",
                        seats: selected.table?.seats || 4,
                        shape: selected.shape || "circle",
                      });
                      setShowEditTable(true);
                    }}
                    className="px-4 py-2.5 rounded-2xl font-extrabold bg-blue-600 text-white hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                )}

                <button
                  onClick={() => selectedItem && deleteItem(selectedItem)}
                  className="px-4 py-2.5 rounded-2xl font-extrabold bg-rose-600 text-white hover:bg-rose-700 transition-colors inline-flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add Table Modal */}
        {showAddTable && (
          <div className="fixed inset-0 z-[100] bg-black/40 p-4 flex items-center justify-center">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg ring-1 ring-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xl font-extrabold text-slate-900">Add New Table</h3>
                <button
                  onClick={() => setShowAddTable(false)}
                  className="p-2 rounded-2xl text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Table Number</label>
                  <input
                    type="number"
                    value={newTable.table_number}
                    onChange={(e) => setNewTable({ ...newTable, table_number: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Enter table number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Seats</label>
                  <input
                    type="number"
                    value={newTable.seats}
                    onChange={(e) => setNewTable({ ...newTable, seats: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-3 rounded-2xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                    min="1"
                    max="20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Shape</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "circle", label: "Round", icon: Circle },
                      { value: "square", label: "Square", icon: Square },
                      { value: "rectangle", label: "Rect", icon: Square },
                    ].map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => setNewTable({ ...newTable, shape: value as any })}
                        className={cx(
                          "p-4 rounded-2xl ring-1 transition-all text-left",
                          newTable.shape === value
                            ? "ring-blue-500 bg-blue-50 text-blue-900"
                            : "ring-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                        )}
                      >
                        <Icon size={22} className="mb-2" />
                        <div className="font-extrabold">{label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowAddTable(false)}
                    className="flex-1 px-4 py-3 rounded-2xl font-extrabold bg-slate-100 text-slate-900 hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={addNewTable}
                    disabled={!newTable.table_number}
                    className="flex-1 px-4 py-3 rounded-2xl font-extrabold bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-600 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Element Modal */}
        {showAddElement && (
          <div className="fixed inset-0 z-[100] bg-black/40 p-4 flex items-center justify-center">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg ring-1 ring-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xl font-extrabold text-slate-900">Add Restaurant Element</h3>
                <button
                  onClick={() => setShowAddElement(false)}
                  className="p-2 rounded-2xl text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {elementTypes.map(({ type, label, icon: Icon }) => (
                      <button
                        key={type}
                        onClick={() => setNewElement({ ...newElement, type: type as any })}
                        className={cx(
                          "p-4 rounded-2xl ring-1 transition-all text-left",
                          newElement.type === type
                            ? "ring-blue-500 bg-blue-50 text-blue-900"
                            : "ring-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                        )}
                      >
                        <Icon size={22} className="mb-2" />
                        <div className="font-extrabold">{label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Label</label>
                  <input
                    type="text"
                    value={newElement.label}
                    onChange={(e) => setNewElement({ ...newElement, label: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder={`Enter ${newElement.type} name`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Width</label>
                    <input
                      type="number"
                      value={newElement.width}
                      onChange={(e) => setNewElement({ ...newElement, width: parseInt(e.target.value) || 150 })}
                      className="w-full px-4 py-3 rounded-2xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                      min="50"
                      max="500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Height</label>
                    <input
                      type="number"
                      value={newElement.height}
                      onChange={(e) => setNewElement({ ...newElement, height: parseInt(e.target.value) || 80 })}
                      className="w-full px-4 py-3 rounded-2xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                      min="30"
                      max="300"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowAddElement(false)}
                    className="flex-1 px-4 py-3 rounded-2xl font-extrabold bg-slate-100 text-slate-900 hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={addNewElement}
                    disabled={!newElement.label && newElement.type !== "wall"}
                    className="flex-1 px-4 py-3 rounded-2xl font-extrabold bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-600 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Table Modal */}
        {showEditTable && (
          <div className="fixed inset-0 z-[100] bg-black/40 p-4 flex items-center justify-center">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg ring-1 ring-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xl font-extrabold text-slate-900">Edit Table</h3>
                <button
                  onClick={() => setShowEditTable(false)}
                  className="p-2 rounded-2xl text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Seats</label>
                  <input
                    type="number"
                    value={newTable.seats}
                    onChange={(e) => setNewTable({ ...newTable, seats: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-3 rounded-2xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                    min="1"
                    max="20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Shape</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "circle", label: "Round", icon: Circle },
                      { value: "square", label: "Square", icon: Square },
                      { value: "rectangle", label: "Rect", icon: Square },
                    ].map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => setNewTable({ ...newTable, shape: value as any })}
                        className={cx(
                          "p-4 rounded-2xl ring-1 transition-all text-left",
                          newTable.shape === value
                            ? "ring-blue-500 bg-blue-50 text-blue-900"
                            : "ring-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                        )}
                      >
                        <Icon size={22} className="mb-2" />
                        <div className="font-extrabold">{label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowEditTable(false)}
                    className="flex-1 px-4 py-3 rounded-2xl font-extrabold bg-slate-100 text-slate-900 hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => selectedItem && deleteItem(selectedItem)}
                    className="flex-1 px-4 py-3 rounded-2xl font-extrabold bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                  >
                    Delete
                  </button>

                  <button
                    onClick={updateTableDetails}
                    className="flex-1 px-4 py-3 rounded-2xl font-extrabold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorplanEditor;
